import authService from './authService.js';
import Menu from '../config/app-menu.jsx';

class SearchService {
  constructor() {
    this.searchableContent = this.initializeSearchableContent();
    this.isInitialized = false;
  }

  // Initialize the search service (called when search dropdown opens)
  initialize() {
    if (!this.isInitialized) {
      // Refresh navigation items in case menu has changed
      this.searchableContent.navigation = this.getNavigationItems();
      this.isInitialized = true;
    }
  }

  // Initialize searchable content based on different categories
  initializeSearchableContent() {
    return {
      // Navigation menu items
      navigation: [],
      
      // Page content and features
      features: [
        {
          id: 'pentest-scan',
          title: 'Pentest Scan',
          description: 'Run security penetration tests on targets',
          keywords: ['security', 'scan', 'vulnerability', 'pentest', 'test'],
          path: '/small-pentest',
          requiredRole: 'user',
          category: 'Security'
        },
        {
          id: 'ai-chat',
          title: 'AI Assistant',
          description: 'Chat with AI for cybersecurity guidance',
          keywords: ['ai', 'chat', 'assistant', 'help', 'guidance'],
          path: '/aichat',
          requiredRole: 'user',
          category: 'AI Tools'
        },
        {
          id: 'agents-management',
          title: 'Agents Management',
          description: 'Manage and monitor security agents',
          keywords: ['agents', 'monitor', 'manage', 'security'],
          path: '/agents',
          requiredRole: 'superuser',
          category: 'Management'
        },
        {
          id: 'user-management',
          title: 'User Management',
          description: 'Manage users, roles and permissions',
          keywords: ['users', 'roles', 'permissions', 'admin', 'manage'],
          path: '/admin/users',
          requiredRole: 'admin',
          category: 'Administration'
        },
        {
          id: 'statistics',
          title: 'Statistics & Analytics',
          description: 'View system statistics and analytics',
          keywords: ['stats', 'analytics', 'reports', 'data', 'metrics'],
          path: '/admin/statistics',
          requiredRole: 'admin',
          category: 'Analytics'
        },
        {
          id: 'dashboard',
          title: 'Dashboard',
          description: 'Administrative dashboard and overview',
          keywords: ['dashboard', 'overview', 'admin', 'summary'],
          path: '/dashboard',
          requiredRole: 'admin',
          category: 'Overview'
        },
        {
          id: 'notifications',
          title: 'Notifications',
          description: 'Test notification system functionality',
          keywords: ['notifications', 'alerts', 'test', 'messages'],
          path: '/test-notifications',
          requiredRole: 'user',
          category: 'Testing'
        },
        {
          id: 'documentation',
          title: 'Documentation',
          description: 'Comprehensive platform documentation and guides',
          keywords: ['documentation', 'docs', 'help', 'guide', 'manual', 'reference'],
          path: '/documentation',
          requiredRole: 'user',
          category: 'Help'
        }
      ],
      
      // Quick actions based on role
      actions: [
        {
          id: 'quick-scan',
          title: 'Quick Scan',
          description: 'Start a quick security scan',
          keywords: ['quick', 'scan', 'fast', 'security'],
          action: () => window.location.href = '/small-pentest',
          requiredRole: 'user',
          category: 'Quick Actions'
        },
        {
          id: 'view-users',
          title: 'View All Users',
          description: 'See all system users',
          keywords: ['users', 'list', 'all', 'view'],
          action: () => window.location.href = '/admin/users',
          requiredRole: 'admin',
          category: 'Quick Actions'
        },
        {
          id: 'system-stats',
          title: 'System Statistics',
          description: 'View system performance metrics',
          keywords: ['system', 'performance', 'metrics', 'stats'],
          action: () => window.location.href = '/admin/statistics',
          requiredRole: 'admin',
          category: 'Quick Actions'
        }
      ],
      
      // Help and documentation
      help: [
        {
          id: 'pentest-help',
          title: 'How to run a pentest',
          description: 'Learn how to configure and run penetration tests',
          keywords: ['help', 'tutorial', 'pentest', 'guide'],
          requiredRole: 'user',
          category: 'Help'
        },
        {
          id: 'user-roles-help',
          title: 'Understanding user roles',
          description: 'Learn about different user roles and permissions',
          keywords: ['roles', 'permissions', 'access', 'help'],
          requiredRole: 'user',
          category: 'Help'
        },
        {
          id: 'admin-help',
          title: 'Admin panel guide',
          description: 'Complete guide for administrators',
          keywords: ['admin', 'guide', 'management', 'help'],
          requiredRole: 'admin',
          category: 'Help'
        }
      ],
      
      // Documentation sections
      documentation: [
        {
          id: 'overview-docs',
          title: 'Platform Overview',
          description: 'Introduction to GuardPoint cybersecurity platform',
          keywords: ['overview', 'introduction', 'platform', 'guardpoint', 'features'],
          path: '/documentation',
          requiredRole: 'user',
          category: 'Documentation'
        },
        {
          id: 'getting-started-docs',
          title: 'Getting Started Guide',
          description: 'Step-by-step guide to using GuardPoint',
          keywords: ['getting started', 'tutorial', 'first steps', 'beginner'],
          path: '/documentation',
          requiredRole: 'user',
          category: 'Documentation'
        },
        {
          id: 'security-testing-docs',
          title: 'Security Testing Guide',
          description: 'Learn about penetration testing and vulnerability assessment',
          keywords: ['security testing', 'pentest', 'vulnerability', 'assessment', 'scanning'],
          path: '/documentation',
          requiredRole: 'user',
          category: 'Documentation'
        },
        {
          id: 'ai-features-docs',
          title: 'AI Features Documentation',
          description: 'Guide to AI-powered security features',
          keywords: ['ai', 'artificial intelligence', 'chat', 'analysis', 'recommendations'],
          path: '/documentation',
          requiredRole: 'user',
          category: 'Documentation'
        },
        {
          id: 'user-roles-docs',
          title: 'User Roles & Permissions',
          description: 'Understanding user roles and access control',
          keywords: ['user roles', 'permissions', 'access control', 'admin', 'superuser'],
          path: '/documentation',
          requiredRole: 'user',
          category: 'Documentation'
        },
        {
          id: 'troubleshooting-docs',
          title: 'Troubleshooting Guide',
          description: 'Common issues and solutions',
          keywords: ['troubleshooting', 'problems', 'issues', 'solutions', 'errors'],
          path: '/documentation',
          requiredRole: 'user',
          category: 'Documentation'
        },
        {
          id: 'api-reference-docs',
          title: 'API Reference',
          description: 'Technical documentation for developers',
          keywords: ['api', 'reference', 'developers', 'endpoints', 'integration'],
          path: '/documentation',
          requiredRole: 'user',
          category: 'Documentation'
        },
        {
          id: 'doc-small-pentest',
          title: 'Small Pentest Documentation',
          description: 'Comprehensive guide for automated penetration testing, security tools, user permissions, and step-by-step usage instructions',
          keywords: ['pentest', 'penetration', 'testing', 'security', 'tools', 'scanning', 'vulnerability', 'assessment', 'permissions', 'roles', 'user', 'superuser', 'admin'],
          path: '/documentation',
          requiredRole: 'user',
          category: 'Documentation'
        }
      ]
    };
  }

  // Get user's current role
  getCurrentUserRole() {
    const user = authService.getCurrentUser();
    return user?.role || 'guest';
  }

  // Check if user has access to specific content
  hasAccess(requiredRole) {
    const userRole = this.getCurrentUserRole();
    
    // Role hierarchy: guest < user < superuser < admin
    const roleHierarchy = {
      'guest': 0,
      'user': 1,
      'superuser': 2,
      'admin': 3
    };
    
    const userLevel = roleHierarchy[userRole] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;
    
    return userLevel >= requiredLevel;
  }

  // Get navigation items based on user role
  getNavigationItems() {
    return Menu.filter(item => {
      if (item.is_header || item.is_divider) return false;
      return !item.requiredRole || this.hasAccess(item.requiredRole);
    });
  }

  // Search through all content
  search(query, options = {}) {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const searchTerm = query.toLowerCase().trim();
    const maxResults = options.maxResults || 20;
    const allResults = [];

    // Search navigation items
    const navItems = this.getNavigationItems();
    const navigationResults = navItems.filter(item => {
      const titleMatch = item.title.toLowerCase().includes(searchTerm);
      const pathMatch = item.path && item.path.toLowerCase().includes(searchTerm);
      return titleMatch || pathMatch;
    }).map(item => ({
      ...item,
      category: 'navigation',
      icon: item.icon || 'bi-compass',
      score: this.calculateNavigationRelevance(item, searchTerm)
    }));

    // Search features
    const featureResults = this.searchInCategory('features', searchTerm, maxResults)
      .map(item => ({ ...item, category: 'features', icon: item.icon || 'bi-gear' }));

    // Search actions
    const actionResults = this.searchInCategory('actions', searchTerm, maxResults)
      .map(item => ({ ...item, category: 'actions', icon: item.icon || 'bi-lightning' }));

    // Search help
    const helpResults = this.searchInCategory('help', searchTerm, maxResults)
      .map(item => ({ ...item, category: 'help', icon: item.icon || 'bi-question-circle' }));

    // Search documentation
    const docResults = this.searchInCategory('documentation', searchTerm, maxResults)
      .map(item => ({ ...item, category: 'documentation', icon: item.icon || 'bi-book' }));

    // Combine all results
    allResults.push(...navigationResults, ...featureResults, ...actionResults, ...helpResults, ...docResults);

    // Sort by relevance score and limit results
    return allResults
      .sort((a, b) => (b.score || b.relevance || 0) - (a.score || a.relevance || 0))
      .slice(0, maxResults);
  }

  // Search within a specific category
  searchInCategory(category, searchTerm, maxResults) {
    const items = this.searchableContent[category] || [];
    
    return items.filter(item => {
      // Check access first
      if (!this.hasAccess(item.requiredRole)) return false;
      
      // Search in title, description, and keywords
      const titleMatch = item.title.toLowerCase().includes(searchTerm);
      const descMatch = item.description.toLowerCase().includes(searchTerm);
      const keywordMatch = item.keywords.some(keyword => 
        keyword.toLowerCase().includes(searchTerm)
      );
      
      return titleMatch || descMatch || keywordMatch;
    })
    .map(item => ({
      ...item,
      // Calculate relevance score
      relevance: this.calculateRelevance(item, searchTerm)
    }))
    .sort((a, b) => b.relevance - a.relevance)
    .slice(0, maxResults);
  }

  // Calculate relevance score for search results
  calculateRelevance(item, searchTerm) {
    let score = 0;
    
    // Title match gets highest score
    if (item.title.toLowerCase().includes(searchTerm)) {
      score += item.title.toLowerCase() === searchTerm ? 100 : 50;
    }
    
    // Description match
    if (item.description && item.description.toLowerCase().includes(searchTerm)) {
      score += 25;
    }
    
    // Keyword matches
    if (item.keywords) {
      item.keywords.forEach(keyword => {
        if (keyword.toLowerCase().includes(searchTerm)) {
          score += keyword.toLowerCase() === searchTerm ? 30 : 15;
        }
      });
    }
    
    return score;
  }

  // Calculate relevance score for navigation items
  calculateNavigationRelevance(item, searchTerm) {
    let score = 0;
    
    // Title match gets highest score
    if (item.title.toLowerCase().includes(searchTerm)) {
      score += item.title.toLowerCase() === searchTerm ? 100 : 50;
    }
    
    // Path match
    if (item.path && item.path.toLowerCase().includes(searchTerm)) {
      score += 30;
    }
    
    return score;
  }

  // Get search suggestions based on user role
  getSuggestions(limit = 5) {
    const userRole = this.getCurrentUserRole();
    
    const suggestionsByRole = {
      'user': [
        { title: 'Pentest Scan', query: 'pentest' },
        { title: 'AI Assistant', query: 'ai chat' },
        { title: 'Documentation', query: 'documentation' },
        { title: 'Dashboard', query: 'dashboard' },
        { title: 'Notifications', query: 'notifications' }
      ],
      'superuser': [
        { title: 'Agents Management', query: 'agents' },
        { title: 'Pentest Scan', query: 'pentest' },
        { title: 'AI Assistant', query: 'ai chat' },
        { title: 'Documentation', query: 'documentation' },
        { title: 'Dashboard', query: 'dashboard' }
      ],
      'admin': [
        { title: 'User Management', query: 'users' },
        { title: 'Statistics', query: 'statistics' },
        { title: 'Dashboard', query: 'dashboard' },
        { title: 'Documentation', query: 'documentation' },
        { title: 'Agents Management', query: 'agents' }
      ],
      'guest': [
        { title: 'Documentation', query: 'documentation' },
        { title: 'Help', query: 'help' },
        { title: 'Login', query: 'login' }
      ]
    };
    
    return (suggestionsByRole[userRole] || suggestionsByRole['guest']).slice(0, limit);
  }

  // Get popular searches based on user role
  getPopularSearches() {
    const userRole = this.getCurrentUserRole();
    
    const popularByRole = {
      'user': ['pentest', 'scan', 'ai chat', 'documentation', 'notifications'],
      'superuser': ['agents', 'pentest', 'scan', 'documentation', 'ai chat'],
      'admin': ['users', 'statistics', 'dashboard', 'documentation', 'agents'],
      'guest': ['documentation', 'help', 'login']
    };
    
    return popularByRole[userRole] || popularByRole['guest'];
  }
}

// Create and export a singleton instance
const searchService = new SearchService();
export default searchService;